import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Package, 
  Plus, 
  AlertTriangle, 
  TrendingDown,
  TrendingUp,
  ShoppingCart,
  Trash2,
  Edit2,
  RefreshCw,
  Search,
  Filter,
  Download,
  Award,
  ArrowDown,
  ArrowUp,
  Minus,
  Truck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SortableHeader, sortData } from "../components/SortableHeader";
import ImportButton from "../components/ImportButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Warenwirtschaft = () => {
  const [lagerbestand, setLagerbestand] = useState([]);
  const [zutaten, setZutaten] = useState([]);
  const [einkauf, setEinkauf] = useState([]);
  const [warnungen, setWarnungen] = useState({ anzahl_warnungen: 0, warnungen: [] });
  const [preisvergleich, setPreisvergleich] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedZutat, setExpandedZutat] = useState(null);
  
  // Dialogs
  const [einkaufDialogOpen, setEinkaufDialogOpen] = useState(false);
  const [inventurDialogOpen, setInventurDialogOpen] = useState(false);
  const [selectedBestand, setSelectedBestand] = useState(null);
  
  // Einkauf Form
  const [einkaufForm, setEinkaufForm] = useState({
    zutat_id: "",
    zutat_name: "",
    menge: "",
    einheit: "",
    einkaufspreis: "",
    lieferant: "",
    lieferdatum: new Date().toISOString().split('T')[0],
    notiz: ""
  });
  
  // Sorting
  const [sortConfigLager, setSortConfigLager] = useState({ key: null, direction: 'asc' });
  const [sortConfigEinkauf, setSortConfigEinkauf] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    try {
      const [bestandRes, zutatenRes, einkaufRes, warnungenRes, preisvergleichRes] = await Promise.all([
        axios.get(`${API}/lagerbestand`, { withCredentials: true }),
        axios.get(`${API}/zutaten`, { withCredentials: true }),
        axios.get(`${API}/einkauf`, { withCredentials: true }),
        axios.get(`${API}/lagerbestand/warnungen`, { withCredentials: true }),
        axios.get(`${API}/einkauf/preisvergleich`, { withCredentials: true })
      ]);
      setLagerbestand(bestandRes.data);
      setZutaten(zutatenRes.data);
      setEinkauf(einkaufRes.data);
      setWarnungen(warnungenRes.data);
      setPreisvergleich(preisvergleichRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const initialisiereLagerbestand = async () => {
    try {
      const res = await axios.post(`${API}/lagerbestand/initialisieren`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Initialisieren");
    }
  };

  const handleZutatSelect = (zutatId) => {
    const zutat = zutaten.find(z => z.id === zutatId);
    if (zutat) {
      setEinkaufForm({
        ...einkaufForm,
        zutat_id: zutat.id,
        zutat_name: zutat.name,
        einheit: zutat.einheit
      });
    }
  };

  const handleEinkaufSubmit = async (e) => {
    e.preventDefault();
    
    if (!einkaufForm.zutat_id || !einkaufForm.menge || !einkaufForm.einkaufspreis) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    try {
      await axios.post(`${API}/einkauf`, {
        ...einkaufForm,
        menge: parseFloat(einkaufForm.menge),
        einkaufspreis: parseFloat(einkaufForm.einkaufspreis)
      }, { withCredentials: true });
      
      toast.success("Wareneingang erfasst");
      setEinkaufDialogOpen(false);
      setEinkaufForm({
        zutat_id: "",
        zutat_name: "",
        menge: "",
        einheit: "",
        einkaufspreis: "",
        lieferant: "",
        lieferdatum: new Date().toISOString().split('T')[0],
        notiz: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleInventurUpdate = async (e) => {
    e.preventDefault();
    
    if (!selectedBestand) return;
    
    try {
      await axios.put(`${API}/lagerbestand/${selectedBestand.id}`, {
        menge: parseFloat(selectedBestand.menge),
        mindestbestand: parseFloat(selectedBestand.mindestbestand || 0),
        lagerort: selectedBestand.lagerort
      }, { withCredentials: true });
      
      toast.success("Bestand aktualisiert");
      setInventurDialogOpen(false);
      setSelectedBestand(null);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const filteredBestand = lagerbestand.filter(item =>
    item.zutat_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="warenwirtschaft-page">
      {/* Header */}
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Warenwirtschaft</h1>
            <p className="page-subtitle">Lagerbestand, Einkauf und Inventur</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={initialisiereLagerbestand}
              className="rounded-sm"
              data-testid="init-lager-btn"
            >
              <RefreshCw size={16} className="mr-2" />
              Initialisieren
            </Button>
            <Button 
              onClick={() => setEinkaufDialogOpen(true)}
              className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
              data-testid="add-einkauf-btn"
            >
              <ShoppingCart size={16} className="mr-2" />
              Wareneingang
            </Button>
            <ImportButton kategorie="einkauf" felder={["zutat_name","menge","einheit","einkaufspreis","lieferant","lieferdatum"]} onImportSuccess={fetchData} label="Rechnung importieren" />
          </div>
        </div>
      </header>

      {/* Warnungen */}
      {warnungen.anzahl_warnungen > 0 && (
        <div className="px-8 py-4">
          <div className="bg-[#FFF3E0] border border-[#FFE0B2] p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-[#E65100] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-[#E65100] text-sm">
                  {warnungen.anzahl_warnungen} Artikel unter Mindestbestand
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {warnungen.warnungen.slice(0, 5).map((w) => (
                    <span key={w.id} className="text-xs bg-[#FFE0B2] px-2 py-1 text-[#BF360C]">
                      {w.zutat_name}: {w.aktueller_bestand} {w.einheit}
                    </span>
                  ))}
                  {warnungen.anzahl_warnungen > 5 && (
                    <span className="text-xs text-[#BF360C]">+{warnungen.anzahl_warnungen - 5} weitere</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-8 py-4">
        <Tabs defaultValue="lager" className="w-full">
          <TabsList className="bg-stone-100 dark:bg-slate-800 p-1 mb-6">
            <TabsTrigger value="lager" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
              <Package size={16} className="mr-2" />Lagerbestand
            </TabsTrigger>
            <TabsTrigger value="einkauf" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
              <ShoppingCart size={16} className="mr-2" />Einkauf-Historie
            </TabsTrigger>
            <TabsTrigger value="preisvergleich" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700" data-testid="preisvergleich-tab">
              <Award size={16} className="mr-2" />Preisvergleich
            </TabsTrigger>
          </TabsList>

          {/* Lagerbestand Tab */}
          <TabsContent value="lager">
            <div className="mb-4 flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#858585]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Zutat suchen..."
                  className="pl-10 border-stone-300 rounded-sm"
                  data-testid="search-lager"
                />
              </div>
            </div>

            {filteredBestand.length === 0 ? (
              <div className="empty-state">
                <Package size={64} className="mx-auto text-[#D1CFC9]" />
                <h3>Kein Lagerbestand</h3>
                <p>Klicken Sie auf "Initialisieren" um den Lagerbestand aus Ihren Zutaten zu erstellen</p>
              </div>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <SortableHeader label="Zutat" sortKey="zutat_name" sortConfig={sortConfigLager} onSort={setSortConfigLager} />
                      <SortableHeader label="Bestand" sortKey="menge" sortConfig={sortConfigLager} onSort={setSortConfigLager} />
                      <SortableHeader label="Mindest." sortKey="mindestbestand" sortConfig={sortConfigLager} onSort={setSortConfigLager} />
                      <SortableHeader label="Status" sortKey="warnung_niedrig" sortConfig={sortConfigLager} onSort={setSortConfigLager} />
                      <SortableHeader label="Lagerort" sortKey="lagerort" sortConfig={sortConfigLager} onSort={setSortConfigLager} />
                      <SortableHeader label="Letzter Einkauf" sortKey="letzter_einkauf" sortConfig={sortConfigLager} onSort={setSortConfigLager} />
                      <th className="text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortData(filteredBestand, sortConfigLager).map((item) => (
                      <tr key={item.id} data-testid={`lager-row-${item.id}`}>
                        <td className="font-medium">{item.zutat_name}</td>
                        <td className="font-mono">{item.menge} {item.einheit}</td>
                        <td className="font-mono text-[#858585]">{item.mindestbestand || 0} {item.einheit}</td>
                        <td>
                          {item.warnung_niedrig ? (
                            <span className="badge bg-[#FFF3E0] text-[#E65100] border border-[#FFE0B2]">
                              <TrendingDown size={12} className="mr-1" />
                              Niedrig
                            </span>
                          ) : (
                            <span className="badge bg-[#E8F5E9] text-[#2E7D32] border border-[#C8E6C9]">
                              OK
                            </span>
                          )}
                        </td>
                        <td className="text-[#858585]">{item.lagerort || '-'}</td>
                        <td className="text-[#858585]">{item.letzter_einkauf || '-'}</td>
                        <td className="text-right">
                          <button 
                            className="btn-icon"
                            onClick={() => {
                              setSelectedBestand({...item});
                              setInventurDialogOpen(true);
                            }}
                          >
                            <Edit2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Einkauf Tab */}
          <TabsContent value="einkauf">
            {einkauf.length === 0 ? (
              <div className="empty-state">
                <ShoppingCart size={64} className="mx-auto text-[#D1CFC9]" />
                <h3>Keine Einkäufe</h3>
                <p>Erfassen Sie Wareneingänge um die Historie zu sehen</p>
              </div>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <SortableHeader label="Datum" sortKey="lieferdatum" sortConfig={sortConfigEinkauf} onSort={setSortConfigEinkauf} />
                      <SortableHeader label="Zutat" sortKey="zutat_name" sortConfig={sortConfigEinkauf} onSort={setSortConfigEinkauf} />
                      <SortableHeader label="Menge" sortKey="menge" sortConfig={sortConfigEinkauf} onSort={setSortConfigEinkauf} />
                      <SortableHeader label="Preis gesamt" sortKey="einkaufspreis" sortConfig={sortConfigEinkauf} onSort={setSortConfigEinkauf} />
                      <SortableHeader label="Preis/Einheit" sortKey="preis_pro_einheit" sortConfig={sortConfigEinkauf} onSort={setSortConfigEinkauf} />
                      <SortableHeader label="Lieferant" sortKey="lieferant" sortConfig={sortConfigEinkauf} onSort={setSortConfigEinkauf} />
                      <th>Notiz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortData(einkauf, sortConfigEinkauf).map((item) => (
                      <tr key={item.id}>
                        <td>{item.lieferdatum}</td>
                        <td className="font-medium">{item.zutat_name}</td>
                        <td className="font-mono">{item.menge} {item.einheit}</td>
                        <td className="font-mono">{formatCurrency(item.einkaufspreis)}</td>
                        <td className="font-mono text-[#858585]">{formatCurrency(item.preis_pro_einheit)}/{item.einheit}</td>
                        <td>{item.lieferant || '-'}</td>
                        <td className="text-[#858585] max-w-[150px] truncate">{item.notiz || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Preisvergleich Tab */}
          <TabsContent value="preisvergleich">
            {!preisvergleich || preisvergleich.anzahl_zutaten === 0 ? (
              <div className="empty-state">
                <Award size={64} className="mx-auto text-[#D1CFC9]" />
                <h3>Kein Preisvergleich möglich</h3>
                <p>Erfassen Sie Einkäufe mit verschiedenen Lieferanten um Preise zu vergleichen</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="preisvergleich-content">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Verglichene Zutaten</p>
                    <p className="text-2xl font-mono font-bold mt-1">{preisvergleich.anzahl_zutaten}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Aktive Lieferanten</p>
                    <p className="text-2xl font-mono font-bold mt-1">{preisvergleich.anzahl_lieferanten}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Preisänderungen</p>
                    <p className="text-2xl font-mono font-bold mt-1">
                      {preisvergleich.vergleich.filter(v => v.preis_trend_prozent !== 0).length}
                    </p>
                  </div>
                </div>

                {/* Zutat Comparison Cards */}
                <div className="space-y-3">
                  {preisvergleich.vergleich.map((item) => (
                    <div 
                      key={item.zutat_id}
                      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
                      data-testid={`vergleich-${item.zutat_id}`}
                    >
                      {/* Main Row */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                        onClick={() => setExpandedZutat(expandedZutat === item.zutat_id ? null : item.zutat_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm">{item.zutat_name}</h4>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                {item.lieferanten.length} Lieferant{item.lieferanten.length !== 1 ? 'en' : ''} · {item.einheit}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            {/* Best Supplier */}
                            <div className="text-right">
                              <p className="text-xs text-slate-500 dark:text-slate-400">Bester Preis</p>
                              <p className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                                {formatCurrency(item.bester_preis)}/{item.einheit}
                              </p>
                              <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 justify-end">
                                <Award size={10} />
                                {item.bester_lieferant}
                              </p>
                            </div>
                            {/* Trend */}
                            <div className="text-right min-w-[70px]">
                              {item.preis_trend_prozent !== 0 ? (
                                <span className={`inline-flex items-center gap-1 text-xs font-mono font-medium px-2 py-1 rounded-full ${
                                  item.preis_trend_prozent > 0 
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                    : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                }`}>
                                  {item.preis_trend_prozent > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                  {Math.abs(item.preis_trend_prozent)}%
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-mono text-slate-400 px-2 py-1">
                                  <Minus size={12} /> stabil
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {expandedZutat === item.zutat_id && (
                        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4">
                          <h5 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 mb-3">
                            Lieferanten-Vergleich
                          </h5>
                          <div className="space-y-2">
                            {item.lieferanten.map((lief, idx) => (
                              <div 
                                key={lief.lieferant}
                                className={`flex items-center justify-between p-3 rounded-lg ${
                                  idx === 0 
                                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800' 
                                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                                }`}
                                data-testid={`lieferant-vergleich-${lief.lieferant}`}
                              >
                                <div className="flex items-center gap-3">
                                  {idx === 0 && <Award size={16} className="text-emerald-600 dark:text-emerald-400" />}
                                  <div>
                                    <p className="font-medium text-sm flex items-center gap-2">
                                      <Truck size={12} className="text-slate-400" />
                                      {lief.lieferant}
                                      {idx === 0 && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full uppercase">Günstigster</span>}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                      {lief.anzahl_einkauf} Einkäufe · Zuletzt: {lief.letztes_datum || '-'}
                                      {lief.liefergebuehren > 0 && ` · Liefergebühr: ${formatCurrency(lief.liefergebuehren)}`}
                                      {lief.lieferzeit_tage && ` · ${lief.lieferzeit_tage} Tage`}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-mono font-semibold text-sm">{formatCurrency(lief.letzter_preis)}/{item.einheit}</p>
                                  <p className="text-[10px] text-slate-400 mt-0.5">
                                    Min: {formatCurrency(lief.min_preis)} · Max: {formatCurrency(lief.max_preis)} · Ø {formatCurrency(lief.durchschnitt_preis)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Price History */}
                          {item.preis_verlauf.length > 1 && (
                            <div className="mt-4">
                              <h5 className="text-xs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 mb-2">
                                Preisverlauf (letzte {item.preis_verlauf.length} Einkäufe)
                              </h5>
                              <div className="flex gap-1 items-end h-16">
                                {item.preis_verlauf.map((pv, pvIdx) => {
                                  const maxP = Math.max(...item.preis_verlauf.map(p => p.preis_pro_einheit));
                                  const minP = Math.min(...item.preis_verlauf.map(p => p.preis_pro_einheit));
                                  const range = maxP - minP || 1;
                                  const height = ((pv.preis_pro_einheit - minP) / range) * 100;
                                  return (
                                    <div
                                      key={pvIdx}
                                      className="flex-1 rounded-t-sm transition-all hover:opacity-80"
                                      style={{
                                        height: `${Math.max(height, 10)}%`,
                                        backgroundColor: pv.lieferant === item.bester_lieferant ? '#10B981' : '#3B82F6',
                                        minWidth: '4px'
                                      }}
                                      title={`${pv.datum}: ${formatCurrency(pv.preis_pro_einheit)}/${item.einheit} (${pv.lieferant})`}
                                    />
                                  );
                                })}
                              </div>
                              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                                <span>{item.preis_verlauf[0]?.datum}</span>
                                <span>{item.preis_verlauf[item.preis_verlauf.length - 1]?.datum}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Wareneingang Dialog */}
      <Dialog open={einkaufDialogOpen} onOpenChange={setEinkaufDialogOpen}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Wareneingang erfassen</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEinkaufSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Zutat *</Label>
                <Select value={einkaufForm.zutat_id} onValueChange={handleZutatSelect}>
                  <SelectTrigger className="border-stone-300 rounded-sm">
                    <SelectValue placeholder="Zutat auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {zutaten.map((z) => (
                      <SelectItem key={z.id} value={z.id}>{z.name} ({z.einheit})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Menge *</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={einkaufForm.menge}
                    onChange={(e) => setEinkaufForm({...einkaufForm, menge: e.target.value})}
                    placeholder="0"
                    className="border-stone-300 rounded-sm font-mono"
                  />
                  <span className="flex items-center px-3 bg-stone-100 border border-stone-300 rounded-sm text-sm">
                    {einkaufForm.einheit || '-'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Einkaufspreis gesamt *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={einkaufForm.einkaufspreis}
                  onChange={(e) => setEinkaufForm({...einkaufForm, einkaufspreis: e.target.value})}
                  placeholder="0.00"
                  className="border-stone-300 rounded-sm font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferant</Label>
                <Input
                  value={einkaufForm.lieferant}
                  onChange={(e) => setEinkaufForm({...einkaufForm, lieferant: e.target.value})}
                  placeholder="z.B. Metro"
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferdatum</Label>
                <Input
                  type="date"
                  value={einkaufForm.lieferdatum}
                  onChange={(e) => setEinkaufForm({...einkaufForm, lieferdatum: e.target.value})}
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Notiz</Label>
                <Input
                  value={einkaufForm.notiz}
                  onChange={(e) => setEinkaufForm({...einkaufForm, notiz: e.target.value})}
                  placeholder="Optional"
                  className="border-stone-300 rounded-sm"
                />
              </div>
            </div>

            {einkaufForm.menge && einkaufForm.einkaufspreis && (
              <div className="bg-stone-50 p-3 border border-stone-200">
                <p className="text-sm text-[#5C5C5C]">
                  Preis pro {einkaufForm.einheit}: 
                  <span className="font-mono font-medium ml-2">
                    {formatCurrency(parseFloat(einkaufForm.einkaufspreis) / parseFloat(einkaufForm.menge))}
                  </span>
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={() => setEinkaufDialogOpen(false)} className="rounded-sm">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm">
                Wareneingang speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inventur Dialog */}
      <Dialog open={inventurDialogOpen} onOpenChange={setInventurDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Bestand anpassen</DialogTitle>
          </DialogHeader>
          
          {selectedBestand && (
            <form onSubmit={handleInventurUpdate} className="space-y-4">
              <p className="text-lg font-medium">{selectedBestand.zutat_name}</p>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Aktueller Bestand</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedBestand.menge}
                    onChange={(e) => setSelectedBestand({...selectedBestand, menge: e.target.value})}
                    className="border-stone-300 rounded-sm font-mono"
                  />
                  <span className="flex items-center px-3 bg-stone-100 border border-stone-300 rounded-sm text-sm">
                    {selectedBestand.einheit}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Mindestbestand</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedBestand.mindestbestand || 0}
                    onChange={(e) => setSelectedBestand({...selectedBestand, mindestbestand: e.target.value})}
                    className="border-stone-300 rounded-sm font-mono"
                  />
                  <span className="flex items-center px-3 bg-stone-100 border border-stone-300 rounded-sm text-sm">
                    {selectedBestand.einheit}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lagerort</Label>
                <Input
                  value={selectedBestand.lagerort || ""}
                  onChange={(e) => setSelectedBestand({...selectedBestand, lagerort: e.target.value})}
                  placeholder="z.B. Kühlraum, Lager"
                  className="border-stone-300 rounded-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
                <Button type="button" variant="outline" onClick={() => setInventurDialogOpen(false)} className="rounded-sm">
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm">
                  Speichern
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Warenwirtschaft;
